import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDeal } from "../lib/store";
import { Card, NumRow, Computed, Tile, ScopeBar } from "../components/shared";
import { dealsApi } from "../lib/api";
import { RollUpPanel } from "../components/RollUpPanel";
import { WorkingCapitalPanel } from "../components/WorkingCapitalPanel";
import { RevenueMixEditor } from "../components/RevenueMix";
import { LeveragePanel } from "../components/LeveragePanel";
import { UploadParsePanel } from "../components/UploadParsePanel";
import { BackLeverCard } from "../components/BackLeverCard";
import { DistributionPolicyCard } from "../components/DistributionPolicyCard";
import { toast } from "sonner";
import * as m from "../lib/model";
import { AuditBadge } from "../components/AuditBadge";
import { qsbsBump } from "../components/room/Qsbs";
import { BlendedEbitdaCard } from "../components/BlendedEbitdaCard";
import { TxnTypeToggle, RecapCard } from "../components/Recap";
import { AssumptionsRail } from "../components/AssumptionsRail";
import { ChevronLeft, ChevronRight, Save } from "lucide-react";

// Typed percent entry that keeps what you are typing (so "0.5" survives the intermediate "0.").
function PctBox({ label, sub, value, onChange, testid }) {
  const [txt, setTxt] = useState(null);
  const shown = txt != null ? txt : value ? String(value) : "";
  return (
    <div className="lev-row">
      <span className="lev-label">{label}{sub && <em className="lev-sub">{sub}</em>}</span>
      <span className="lev-input">
        <input className="inp num" type="text" inputMode="decimal" value={shown}
          onChange={(e) => {
            const v = e.target.value.replace(/[^0-9.]/g, "");
            setTxt(v);
            const n = Number(v);
            onChange(v === "" || isNaN(n) ? 0 : Math.min(100, n));
          }}
          onBlur={() => setTxt(null)} data-testid={testid} />
        <i>%</i>
      </span>
    </div>
  );
}

export default function Assumptions() {
  const { M, V, scope, setScope, scopeOpts, setField, patch, dealId, setDealId, dealName, navigate, asmpSection, setAsmpSection } = useDeal();
  const wfC = m.lpWaterfall(V);
  const gpPct = Math.min(100, Math.max(0, Number(M.gpCallPct) || 0));
  const recap = m.isRecap(M);
  const consolidated = m.isRollupScope(V);
  const addonView = scope.startsWith("addon:");
  const viewTag = m.scopeLabel(M, scope);
  const dscr = m.dscrYr1(V);
  const ex = m.exitReturns(V);
  const asmBump = qsbsBump(V);
  const [saving, setSaving] = useState(false);
  const [sel, setSel] = useState("deal-info");
  useEffect(() => { if (asmpSection) { setSel(asmpSection); setAsmpSection(null); } }, [asmpSection, setAsmpSection]);
  const saveDeal = async () => {
    setSaving(true);
    try {
      const payload = { name: dealName || M.dealName, model: M, verdict: m.verdict(M) };
      if (dealId) { await dealsApi.update(dealId, payload); toast.success("Deal updated in pipeline"); }
      else { const d = await dealsApi.create({ ...payload, stage: "Underwriting" }); setDealId(d.id); toast.success("Deal saved — retrieve it under Pipeline"); }
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  const mRef = useRef(M);
  mRef.current = M;
  // Stable component identity (defined once) so re-renders never remount the input mid-typing.
  const S = useCallback((props) => (
    <NumRow {...props} value={mRef.current[props.k]} onChange={(v) => setField(props.k, v)} />
  ), [setField]);

  const purchaseId = "card-purchase";
  const sections = [
    { id: "deal-info", label: "Deal & Analyst Info" },
    { id: "upload-parse", label: "Upload & Parse" },
    { id: purchaseId, label: recap ? "Recapitalization" : "Purchase & Deal" },
    { id: "card-slb", label: "SLB & Financing" },
    { id: "card-seller-note", label: "Seller Note" },
    { id: "card-rollover", label: "Rollover Equity" },
    { id: "card-operating", label: "Operating, Exit & Mgmt Fee" },
    { id: "blend-card", label: "Blended EBITDA" },
    { id: "revenue-mix", label: "Revenue Mix" },
    { id: "leverage", label: "Leverage" },
    { id: "working-capital", label: "Working Capital" },
    { id: "lp-terms", label: "LP / GP Waterfall" },
    { id: "distribution", label: "Distribution Policy" },
    { id: "transaction-costs", label: "Transaction Costs" },
    { id: "scenario-assumptions", label: "Scenario Assumptions" },
    { id: "rollup-terms", label: "Roll-Up — Platform & Add-ons" },
    { id: "back-lever", label: "Back-Lever Refinance" },
  ];
  const tops = sections;
  const idx = tops.findIndex((s) => s.id === sel);
  const cur = tops[idx] || tops[0];
  const pick = (s) => setSel(s.id);

  return (
    <div className="page" data-testid="page-assumptions">
      <div className="page-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div><div className="page-title">Assumptions{viewTag && <span className="view-tag" data-testid="asmp-view-tag">{viewTag}</span>}</div><div className="page-sub">{consolidated ? "Roll-Up view — the preview below consolidates the platform with every add-on; inputs still edit the platform model" : addonView ? `${V.companyName} standalone — derived from its Roll-Up row (edit it under Roll-Up Terms & Add-ons)` : "Pick an input section on the left — every slider still cascades live through all pages"}</div></div>
        <div className="head-ctls"><AuditBadge V={V} /><ScopeBar M={M} value={scope} onChange={setScope} testid="asmp-scope" /><button className="btn btn-primary" onClick={saveDeal} disabled={saving} data-testid="assumptions-save-deal">{saving ? "Saving…" : dealId ? "Update Current Deal" : "Save Current Deal"}</button></div>
      </div>

      <div className="asmp-layout">
      <AssumptionsRail M={V} sections={sections} active={sel} onSelect={pick} />
      <div className="asmp-body" data-testid="asmp-body">
      <div className="asmp-crumb" data-testid="asmp-crumb">
        <span className="ac-idx">{idx + 1} / {tops.length}</span>
        <span className="ac-lbl">{cur.label}</span>
        <div className="ac-nav">
          <button className="btn" disabled={idx <= 0} onClick={() => setSel(tops[idx - 1].id)} data-testid="asmp-prev"><ChevronLeft size={13} />Prev</button>
          <button className="btn" disabled={idx >= tops.length - 1} onClick={() => setSel(tops[idx + 1].id)} data-testid="asmp-next">Next<ChevronRight size={13} /></button>
        </div>
      </div>

      {sel === "deal-info" && (
      <Card title="Deal & Analyst Info" testid="deal-info">
        <div className="info-row">
          <div className="field"><label>Deal name</label><input className="inp" value={M.dealName || ""} onChange={(e) => setField("dealName", e.target.value)} data-testid="di-dealName" /></div>
          <div className="field"><label title="Drives comps, closed deals, lenders and the teaser">Industry</label><input className="inp" value={M.industry || ""} onChange={(e) => setField("industry", e.target.value)} placeholder="e.g. Commercial HVAC services" data-testid="di-industry" /></div>
          <div className="field"><label>Analyst / firm name</label><input className="inp" value={M.analystName || ""} onChange={(e) => setField("analystName", e.target.value)} data-testid="di-analystName" /></div>
          <div className="field"><label>Contact name (XYZ Ventures)</label><input className="inp" value={M.analystContact || ""} onChange={(e) => setField("analystContact", e.target.value)} data-testid="di-contactName" /></div>
          <div className="field"><label>Email</label><input className="inp" type="email" value={M.analystEmail || ""} onChange={(e) => setField("analystEmail", e.target.value)} data-testid="di-email" /></div>
          <div className="field"><label>Date</label><input className="inp" type="date" value={M.prepDate || ""} onChange={(e) => setField("prepDate", e.target.value)} data-testid="di-date" /></div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14, flexWrap: "wrap" }}>
          <button className="btn btn-primary" onClick={saveDeal} disabled={saving} data-testid="di-save-pipeline">
            <Save size={13} style={{ marginRight: 6 }} />{saving ? "Saving…" : dealId ? "Update in Pipeline" : "Save to Pipeline"}
          </button>
          <span className="ra-sub" data-testid="di-save-state">{dealId ? "Saved to pipeline — changes auto-sync to the Pipeline card" : "Not in the pipeline yet — save to track this deal through stages"}</span>
        </div>
      </Card>
      )}

      {sel === purchaseId && (
      <>
      <div className="txn-bar" data-testid="txn-bar">
        <TxnTypeToggle M={M} patch={patch} />
        <span className="ra-sub">{recap ? "Recapitalizing a platform you already own: refinance, cash out / roll legacy investors, raise new LP equity and a war chest for add-ons." : "Buying a business: purchase price, SBA/senior debt, seller note and sponsor equity."}</span>
      </div>
      {recap ? (
        <RecapCard M={M} setField={setField} patch={patch} S={S} onRollUp={() => navigate("rollup")} />
      ) : (
        <Card title="1 · Purchase & Deal" testid="card-purchase">
          <div className="grid g2 ops-grid">
            <div>
              <S label="Purchase price" k="askPrice" min={200000} max={2000000} step={5000} format={m.fmt$} testid="s-askPrice" />
              <S label="Stated EBITDA (TTM Adj. SDE)" k="sde" min={0} max={800000} step={1000} format={m.fmt$} testid="s-sde" />
              <S label="TTM revenue" k="ttmRevenue" min={0} max={50000000} step={50000} format={m.fmt$} testid="s-ttmRevenue" />
              <S label="Gross margin %" k="grossMarginPct" min={0} max={90} step={0.5} format={(v) => `${v}%`} testid="s-grossMarginPct" />
              <S label="Down payment %" k="downPct" min={5} max={50} step={1} format={(v) => `${v}%`} testid="s-downPct" />
            </div>
            <div>
              <S label="Loan term (yrs)" k="loanYrs" min={5} max={25} step={1} format={(v) => `${v} yr`} testid="s-loanYrs" />
              <S label="Interest rate %" k="rate" min={4} max={16} step={0.25} format={(v) => `${v}%`} testid="s-rate" />
              <Computed label="Loan amount" value={m.fmt$(m.loanAmt(M))} />
              <Computed label="Annual debt service (senior)" value={m.fmt$(m.annualDS(M))} />
            </div>
          </div>
        </Card>
      )}
      </>
      )}

      {sel === "card-slb" && (
      <>
        <Card title="2 · SLB & Financing" testid="card-slb">
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }} data-testid="slb-toggle">
            <input type="checkbox" checked={M.slbOn} onChange={(e) => setField("slbOn", e.target.checked)} />
            Flip RE to investor (Sale-Leaseback)
            <span className={`pill ${M.slbOn ? "go" : "pass"}`} style={{ marginLeft: "auto" }}>{M.slbOn ? "SLB ON" : "SLB OFF"}</span>
          </label>
          {M.slbOn && (
            <div className="grid g2 ops-grid" style={{ marginTop: 10 }}>
              <div>
                <S label="Real estate value" k="reVal" min={0} max={2000000} step={5000} format={m.fmt$} testid="s-reVal" />
                <S label="SLB proceeds" k="slbProceeds" min={0} max={2500000} step={5000} format={m.fmt$} testid="s-slbProceeds" />
              </div>
              <div>
                <Computed label="Net basis reduction" value={m.fmt$(M.slbProceeds - M.reVal)} tone={M.slbProceeds - M.reVal >= 0 ? "go" : "pass"} />
                <Computed label="Net acquisition cost" value={m.fmt$(m.nac(M))} />
              </div>
            </div>
          )}
        </Card>
        {M.slbOn && (
          <Card title="2b · Rent / Lease Terms" style={{ marginTop: 12 }} testid="card-rent">
            <div className="grid g3 ops-grid">
              <div>
                <div className="section-label">Base rent</div>
                <S label="Building SF" k="sqft" min={500} max={20000} step={100} format={(v) => `${v.toLocaleString()} SF`} testid="s-sqft" />
                <S label="$/SF/yr" k="rentRate" min={4} max={40} step={0.5} format={(v) => `$${v}`} testid="s-rentRate" />
                <Computed label="Annual rent" value={m.fmt$(m.annualRent(M))} />
              </div>
              <div>
                <div className="section-label">Escalation</div>
                <S label="Escalator %/yr" k="rentEsc" min={0} max={6} step={0.25} format={(v) => `${v}%`} testid="s-rentEsc" />
                <S label="Initial term (yrs)" k="leaseYrs" min={3} max={20} step={1} format={(v) => `${v} yr`} testid="s-leaseYrs" />
              </div>
              <div>
                <div className="section-label">Structure</div>
                <div className="field"><label>Lease type</label>
                  <select className="inp" value={M.leaseType} onChange={(e) => setField("leaseType", e.target.value)} data-testid="s-leaseType">
                    <option>NNN</option><option>Gross</option><option>Modified</option>
                  </select>
                </div>
              </div>
            </div>
          </Card>
        )}
      </>
      )}

      {sel === "card-seller-note" && (
      <Card title="3 · Seller Note" testid="card-seller-note">
        <div className="grid g2 ops-grid">
          <div>
            <S label="Seller note amount" k="snAmt" min={0} max={500000} step={5000} format={m.fmt$} testid="s-snAmt" />
            <S label="Seller note rate %" k="snRate" min={0} max={15} step={0.5} format={(v) => `${v}%`} testid="s-snRate" />
            <S label="Seller note term (yrs)" k="snYrs" min={1} max={10} step={1} format={(v) => `${v} yr`} testid="s-snYrs" />
          </div>
          <div>
            <Computed label="Seller note annual payment" value={m.fmt$(m.snAnnualDS(M))} />
            <Computed label="Revised senior / SBA loan" value={m.fmt$(m.loanAmt(M))} />
            <Computed label="Total combined annual debt service" value={m.fmt$(m.totalAnnualDS(M))} />
          </div>
        </div>
        <AddOnSellerNotes M={M} setField={setField} />
      </Card>
      )}

      {sel === "card-rollover" && (
      <Card title="4 · Rollover Equity" testid="card-rollover">
        <div className="grid g2 ops-grid">
          <div>
            {!recap && <S label="Rollover equity %" k="rollPct" min={0} max={60} step={1} format={(v) => `${v}%`} testid="s-rollPct" />}
            <Computed label={recap ? "Legacy investors — rolled equity $" : "Seller rollover equity $"} value={m.fmt$(recap ? m.recapModel(M).rolled : m.rolloverAmt(M))} />
            {!recap && <Computed label="Rollover % of total equity (ownership)" value={m.fmtPct(ex.rollOwnPct || 0)} tone={ex.rollOwnPct > 50 ? "pass" : undefined} />}
            {!recap && <div className="ra-sub" style={{ marginTop: 6 }} data-testid="rollover-own-note">The slider is a % of the <b>purchase price</b>; ownership is the rolled dollars over <b>all the equity</b> in the deal — {m.fmt$(m.rolloverAmt(M))} of {m.fmt$(m.equitySourcesTotal(M))} = {m.fmtPct(ex.rollOwnPct || 0)}, exactly as the Sources — Equity table reads. Debt and the seller note fund the rest of the price and own nothing. The rolled dollars are capped at the {m.fmt$(m.downAmt(M))} down payment{m.rolloverClamped(M) ? " — currently capped" : ""} so Sources always balance.</div>}
            {recap && <Computed label="Legacy rollover % of post-recap common" value={m.fmtPct((1 - m.recapModel(M).newLpOwnership) * 100)} />}
            {recap && <div className="ra-sub" style={{ marginTop: 6 }} data-testid="rollover-recap-note">In a recap the rolled amount is the legacy equity value that is <b>not</b> cashed out — driven by the recap multiple, existing debt and cash-out % on the Recapitalization card, not by a rollover %. Edit it there.</div>}
          </div>
          <div>
            <Computed label="Purchase equity (down − rollover)" value={m.fmt$(m.buyerCashIn(M))} />
            <Computed label="Plus: transaction expenses" value={m.fmt$(M.transExpenses || 0)} tone="pass" />
            <Computed label="Plus: transaction costs (fees)" value={m.fmt$(m.transCostsTotal(M))} tone="pass" />
            <Computed label="Buyer net cash required (incl. costs)" value={m.fmt$(ex.equityInvested)} />
          </div>
        </div>
        <div className="section-label" style={{ marginTop: 12 }}>Future add-on sellers — rollover at each acquisition</div>
        <div className="ra-sub" style={{ marginBottom: 8 }}>Driven by the roll-up rollover % (Assumptions → Roll-Up → LBO Capitalization). Every add-on seller keeps this share of their price as equity in the platform, reducing the cash we raise.</div>
        <table className="tbl" data-testid="rollover-addons">
          <thead><tr><th>Add-on</th><th className="num">Closes</th><th className="num">Price</th><th className="num">Rollover %</th><th className="num">Seller rollover $</th><th className="num">Seller note</th><th className="num">Sponsor equity</th></tr></thead>
          <tbody>
            {m.addonTableRows(M).map((r, i) => (
              <tr key={i} className={r.selfFunded ? "sf-row" : ""} data-testid={`rollover-addon-${i}`}>
                <td>{r.name}</td><td className="num mono">{r.yearLabel || `Yr ${r.year}`}</td><td className="num">{m.fmt$(r.price)}</td>
                <td className="num">{m.rollupCfgFor(M).rolloverPct}%</td>
                <td className="num">{m.fmt$(r.rollover)}</td><td className="num">{m.fmt$(r.sellerNote)}</td><td className="num">{m.fmt$(r.equity)}</td>
              </tr>
            ))}
            {(() => { const a = m.addonTableRows(M); return a.length === 0
              ? <tr><td colSpan={7} style={{ color: "var(--text3)", fontSize: 12 }}>No add-ons yet — add them under Assumptions → Roll-Up.</td></tr>
              : <tr className="total"><td>All add-on sellers</td><td className="num">—</td><td className="num">{m.fmt$(a.reduce((x, r) => x + r.price, 0))}</td><td className="num">—</td><td className="num">{m.fmt$(a.reduce((x, r) => x + r.rollover, 0))}</td><td className="num">{m.fmt$(a.reduce((x, r) => x + r.sellerNote, 0))}</td><td className="num">{m.fmt$(a.reduce((x, r) => x + r.equity, 0))}</td></tr>; })()}
          </tbody>
        </table>
      </Card>
      )}

      {sel === "card-operating" && (
      <Card title="5 · Operating Assumptions, Exit & Management Fee" testid="card-operating">
        <div className="grid g3 ops-grid">
          <div data-testid="sec-operations">
            <div className="section-label">Operations</div>
          <S label="SDE growth %/yr" k="sdeGrowth" min={-10} max={20} step={0.5} format={(v) => `${v}%`} testid="s-sdeGrowth" />
          <S label="New owner salary" k="salary" min={0} max={200000} step={1000} format={m.fmt$} testid="s-salary" />
          <S label="Annual CapEx" k="capex" min={0} max={150000} step={1000} format={m.fmt$} testid="s-capex" />
          </div>
          <div data-testid="sec-hold-exit">
            <div className="section-label">Hold & exit</div>
          <S label="Hold period (yrs)" k="hold" min={3} max={20} step={1} format={(v) => (M.holdIndefinite ? "Indefinite" : `${v} yr`)} testid="s-hold" />
          <div className="asmp-row" style={{ borderBottom: "none", paddingTop: 2 }}>
            <span className="lbl">Exit horizon</span>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {[5, 7, 10, 15].map((n) => (
                <button key={n} className="btn" style={{ padding: "5px 12px", ...(!M.holdIndefinite && M.hold === n ? { background: "var(--lime-fill)", color: "var(--on-fill)", borderColor: "var(--lime-fill)", fontWeight: 700 } : {}) }} onClick={() => { setField("holdIndefinite", false); setField("hold", n); }} data-testid={`hold-${n}`}>{n} yr</button>
              ))}
              <button className="btn" style={{ padding: "5px 12px", ...(M.holdIndefinite ? { background: "var(--lime-fill)", color: "var(--on-fill)", borderColor: "var(--lime-fill)", fontWeight: 700 } : {}) }} onClick={() => setField("holdIndefinite", true)} data-testid="hold-indef">Indefinite</button>
            </div>
          </div>
          {M.holdIndefinite && <div style={{ fontSize: 11, color: "var(--text3)", marginTop: -2, marginBottom: 6 }}>Indefinite hold — no sale modeled; returns are cash-on-cash only over a 25-yr horizon.</div>}
          <S label="Exit multiple" k="exitMult" min={1.5} max={8} step={0.25} format={m.fmtX} testid="s-exitMult" />
          {rollupAddonRows(M).length > 0 && (
            <Computed label="Roll-Up consolidated exit multiple" value={m.fmtX(m.rollupCfgFor(M).exitMultiple)} tone="go" />
          )}
          </div>
          <div data-testid="sec-mgmt-fee">
          <div className="section-label">Management fee</div>
          <S label="Management fee %/yr" k="mgmtFeePct" min={0} max={10} step={0.25} format={(v) => `${v}%`} testid="s-mgmtFeePct" />
          <div className="asmp-row" style={{ borderBottom: "none", paddingBottom: 0 }}>
            <span className="lbl">Basis / frequency</span>
            <select className="inp" style={{ flex: "1 1 100px", width: "auto" }} value={M.mgmtFeeBasis} onChange={(e) => setField("mgmtFeeBasis", e.target.value)} data-testid="s-mgmtFeeBasis">
              <option value="EBITDA">% of EBITDA</option>
              <option value="Gross Revenue">% of Gross Revenue</option>
            </select>
            <select className="inp" style={{ flex: "0 1 120px", width: "auto" }} value={M.mgmtFeeFreq} onChange={(e) => setField("mgmtFeeFreq", e.target.value)} data-testid="s-mgmtFeeFreq">
              <option value="Monthly">Monthly</option>
              <option value="Quarterly">Quarterly</option>
            </select>
          </div>
          {M.mgmtFeePct > 0 && (() => {
            const gross = M.mgmtFeeBasis === "Gross Revenue";
            const VV = consolidated ? V : M;
            const committed = (VV.addons || []).filter((c) => !c.selfFunded);
            const sfRow = consolidated ? m.sfGroupRow(VV) : null;
            const ratio = gross && M.sde > 0 ? (M.ttmRevenue || 0) / M.sde : 1;
            const platBase = gross ? (M.ttmRevenue || 0) : m.trueEarnings(M);
            const comBase = committed.reduce((a, c) => a + (c.ebitda || 0), 0) * ratio;
            const sfBase = sfRow ? sfRow.ebitda * ratio : 0;
            const runRate = platBase + comBase + sfBase;
            const pct = (M.mgmtFeePct || 0) / 100;
            const addBase = comBase + sfBase;
            const parts = [committed.length ? `${committed.length} committed` : null, sfRow ? `${sfRow.count} self-funded` : null].filter(Boolean).join(" + ");
            return (
              <>
                <Computed compact label={`Fee base — platform ${gross ? "revenue" : "EBITDA"}`} value={m.fmt$(platBase)} />
                {addBase > 0.5 && <Computed compact label={`+ add-ons at run-rate (${parts})${gross ? " · rev. at platform margin" : ""}`} value={m.fmt$(addBase)} tone="go" />}
                <Computed compact label={`Mgmt fee · Yr 1 → full run-rate (${M.mgmtFeePct}%)`} value={addBase > 0.5 ? `${m.fmt$(m.mgmtFee(VV))} → ${m.fmt$(runRate * pct)}` : m.fmt$(m.mgmtFee(VV))} tone="pass" />
                <Computed compact label={`Per-${M.mgmtFeeFreq === "Monthly" ? "month" : "quarter"} payment · Yr 1`} value={m.fmt$(m.mgmtFeePerPeriod(VV))} />
              </>
            );
          })()}
          </div>
        </div>
      </Card>
      )}

      {sel === "blend-card" && <BlendedEbitdaCard M={M} setField={setField} />}

      {sel === "lp-terms" && (
      <Card title="6 · LP / GP Waterfall Terms" testid="lp-terms">
        {/* Commitment figures read off the same waterfall the Returns page renders. */}
        <div className="cmt-grid">
          <div className="cmt-panel" data-testid="sec-commitment">
            <div className="section-label">{m.lpFundsAtClose(M) ? "Committed add-on equity — funded at close" : "Commitment — additional equity"}</div>
            <PctBox label="GP share of add-on equity" sub="the rest is the LPs'" value={M.gpCallPct}
              onChange={(v) => setField("gpCallPct", v)} testid="s-gpCallPct" />
            <Computed label={`Add-on equity on the ${(V.addons || []).filter((c) => !c.selfFunded && !c.cashFunded).length || "committed"} committed add-ons`} value={m.fmt$(wfC.addlEquity)} testid="c-addonEquity" />
            <Computed label={`GP share (${gpPct}%)`} value={m.fmt$(wfC.gpCalledTotal)} testid="c-gpCommit" />
            <Computed label={`LP share (${100 - gpPct}%)`} value={m.fmt$(wfC.addlEquity - wfC.gpCalledTotal)} testid="c-lpCommit" />
            {m.lpFundsAtClose(M)
              ? <Computed label="Total LP equity at close (platform + add-ons)" value={m.fmt$(wfC.lpEquity0)} tone="go" testid="c-lpInvested" />
              : <>
                <Computed label="Called in the year each add-on closes" value={m.fmt$(wfC.newMoneyTotal)} testid="c-calledTotal" />
                <Computed label="LP invested capital (Day 0 + calls)" value={m.fmt$(wfC.lpEquity)} testid="c-lpInvested" />
              </>}
            <div className="ra-sub cmt-note">{m.lpFundsAtClose(M)
              ? <>Price − senior debt − seller note − seller rollover + working capital + fees on every committed add-on. LPs fund it in the same closing check as the platform equity and it sits in escrow until each add-on closes — no later capital calls. Self-funded add-ons are paid from retained cash and are not part of this number. Switch to capital calls under Distribution Policy → LP funding.</>
              : <>Additional equity is everything the add-ons need beyond the platform close, committed in full on Day 1 and drawn as each add-on closes. The GP's share is a straight % of that total — a war chest, if you switch retained cash to one under Distribution Policy, only offsets the LP's own call, never the commitment. The GP's share is excluded from the LP's pref and invested capital.</>}</div>
          </div>

          <div className="cmt-panel" data-testid="sec-lp-economics">
            <div className="section-label">LP economics</div>
            <S label="LP equity raised" k="lpEquity" format={(v) => (v ? m.fmt$(v) : "Auto")} testid="s-lpEquity" />
            <S label="Preferred return %/yr" k="prefReturn" format={(v) => `${v}%`} testid="s-prefReturn" />
            <S label="LP profit split after pref %" k="lpSplit" format={(v) => `${v}%`} testid="s-lpSplit" />
            <Computed label="GP promote (tier 1)" value={`${100 - (M.lpSplit == null ? 80 : M.lpSplit)}%`} />
            <Computed label="LP equity base used" value={m.fmt$(m.lpEquityBase(M))} />
          </div>

          <div className="cmt-panel" data-testid="sec-stepups">
            <div className="section-label">MOIC step-ups</div>
            <S label="1st step-up hurdle" k="moicStepUp" format={(v) => (v ? m.fmtX(v) : "None")} testid="s-moicStepUp" />
            <S label="GP promote after 1st %" k="gpPromoteStep" format={(v) => `${v}%`} testid="s-gpPromoteStep" />
            <S label="2nd step-up hurdle" k="moicStepUp2" format={(v) => (v ? m.fmtX(v) : "None")} testid="s-moicStepUp2" />
            <S label="GP promote after 2nd %" k="gpPromoteStep2" format={(v) => `${v}%`} testid="s-gpPromoteStep2" />
            <S label="Target MOIC" k="moicTarget" format={m.fmtX} testid="s-moicTarget" />
          </div>

          <div className="cmt-panel" data-testid="sec-seller-common">
            <div className="section-label">Liquidation preference & seller common</div>
            <S label="LP liquidation preference" k="liqPrefX" min={0} max={5} step={0.25} format={(v) => (v ? `${m.fmtX(v)} invested` : "None")} testid="s-liqPrefX" />
            <div className="asmp-row"><span className="lbl">Preference type</span>
              <select className="inp" value={M.liqPrefType === "part" ? "part" : "nonpart"} onChange={(e) => setField("liqPrefType", e.target.value)} data-testid="sel-liqPrefType">
                <option value="nonpart">Non-participating (greater of pref or pro-rata)</option>
                <option value="part">Participating (pref, then shares pro-rata)</option>
              </select></div>
            <Computed label="Preference at exit (incl. unpaid pref)" value={m.fmt$(ex.liqPref || 0)} />
            <Computed label="Still due at exit (interim cash counts)" value={m.fmt$(ex.liqPrefDue || 0)} />
            <Computed label="Seller rolled common — ownership" value={ex.rollOwnPct > 0.01 ? `${m.fmtPct(ex.rollOwnPct)} of equity · ${m.fmt$(ex.rollExit || 0)} at exit` : "No rollover"} />
            <div className="ra-sub cmt-note">Capital stack: senior debt → LP preferred (accrued pref, return of capital, then the {m.fmtX(ex.liqPrefX || 0)} liquidation preference) → GP promote → rolled common. The roll is common / a profits interest by design, so it sees nothing until the LP clears its preference and the promote is paid, and never more than its ownership % of exit equity.</div>
          </div>
        </div>
        <div className="bullet" style={{ marginTop: 12, marginBottom: 0 }}>Waterfall: preferred return → return of LP capital → {(M.liqPrefX == null ? 1 : M.liqPrefX) > 0 ? `${m.fmtX(M.liqPrefX == null ? 1 : M.liqPrefX)} LP liquidation preference (${M.liqPrefType === "part" ? "participating" : "non-participating"}) → ` : ""}{M.lpSplit == null ? 80 : M.lpSplit}/{100 - (M.lpSplit == null ? 80 : M.lpSplit)} LP/GP split{M.moicStepUp ? `, stepping to ${100 - M.gpPromoteStep}/${M.gpPromoteStep} above ${m.fmtX(M.moicStepUp)}` : ""}{M.moicStepUp && M.moicStepUp2 > M.moicStepUp ? `, then ${100 - M.gpPromoteStep2}/${M.gpPromoteStep2} above ${m.fmtX(M.moicStepUp2)}` : ""} LP MOIC → rolled seller common last. See the Returns page for the full LP/GP schedule &amp; investor calculator.</div>
      </Card>
      )}

      {sel === "transaction-costs" && (
      <Card title="7 · Transaction Costs" testid="transaction-costs">
        <div className="bullet">Deal fees & closing costs, layered on top of the purchase price. The acquisition fee, closing costs and due diligence are a % of the <b>acquisition cost</b> ({m.fmt$(M.askPrice)}); the equity fee is a % of the <b>equity placement</b> ({m.fmt$(m.equityPlacement(M))}) and the debt fee a % of the <b>debt placement</b> ({m.fmt$(m.debtPlacement(M))}). The same schedule is charged again on every add-on you buy. All roll into the net acquisition cost and the equity required.</div>
        <div className="grid g2">
          <div data-testid="sec-fees-costs">
            <div className="section-label">Fees & costs</div>
            <S label="Acquisition fee %" k="acqFeePct" min={0} max={10} step={0.1} format={(v) => `${v}%`} testid="s-acqFeePct" />
            <S label="Closing costs %" k="closingCostPct" min={0} max={10} step={0.1} format={(v) => `${v}%`} testid="s-closingCostPct" />
            <S label="Due diligence % (of acquisition cost)" k="ddPct" min={0} max={5} step={0.05} format={(v) => `${v}%`} testid="s-ddPct" />
          </div>
          <div data-testid="sec-placement">
            <div className="section-label">% of placement</div>
            <S label="Equity placement fee % (of equity)" k="equityFeePct" min={0} max={10} step={0.1} format={(v) => `${v}%`} testid="s-equityFeePct" />
            <S label="Debt placement fee % (of debt)" k="debtFeePct" min={0} max={10} step={0.1} format={(v) => `${v}%`} testid="s-debtFeePct" />
            <Computed label="Acquisition fee" value={m.fmt$(m.transactionCosts(M).acqFee)} />
            <Computed label="Due diligence" value={m.fmt$(m.transactionCosts(M).ddCost)} />
            <Computed label="Closing costs" value={m.fmt$(m.transactionCosts(M).closingCost)} />
            <Computed label="Equity placement fee" value={m.fmt$(m.transactionCosts(M).equityFee)} />
            <Computed label="Debt placement fee" value={m.fmt$(m.transactionCosts(M).debtFee)} />
          </div>
        </div>
        <Computed label="Total transaction costs — platform" value={m.fmt$(m.transCostsTotal(M))} tone="pass" />
        <Computed label="Net acquisition cost (incl. costs)" value={m.fmt$(m.nac(M))} />
        <AddOnCostsTable M={M} />
      </Card>
      )}

      {sel === "scenario-assumptions" && (
      <Card title="Scenario Assumptions — Base · Upside · Downside" testid="scenario-assumptions">
        <div className="bullet">The <b>Base</b> case uses your revenue/SDE growth of <b>{M.sdeGrowth}%/yr</b> and a {m.fmtX(M.exitMult)} exit. Set how the <b>Upside</b> (revenue increases) and <b>Downside</b> (revenue drops) cases flex growth and the exit multiple — the Returns &amp; Sensitivity pages apply these whenever you toggle scenarios.</div>
        <div className="grid g2">
          <div data-testid="sec-upside">
            <div className="section-label">Upside — revenue increases</div>
            <S label="Upside SDE / revenue growth %/yr" k="upGrowth" min={-5} max={20} step={0.5} format={(v) => `${v}%`} testid="s-upGrowth" />
            <S label="Upside exit multiple boost" k="upExitDelta" min={0} max={3} step={0.25} format={(v) => `+${v}×`} testid="s-upExitDelta" />
          </div>
          <div data-testid="sec-downside">
            <div className="section-label">Downside — revenue drops</div>
            <S label="Downside SDE / revenue growth %/yr" k="downGrowth" min={-15} max={10} step={0.5} format={(v) => `${v}%`} testid="s-downGrowth" />
            <S label="Downside exit multiple cut" k="downExitDelta" min={0} max={3} step={0.25} format={(v) => `-${v}×`} testid="s-downExitDelta" />
          </div>
        </div>
        {rollupAddonRows(M).length > 0 && (
          <div className="bullet" data-testid="scen-rollup-note">
            Consolidated roll-up: the same growth flexes <b>add-on EBITDA</b> as well as the platform, and the scenario
            moves the roll-up exit multiple with it — base {m.fmtX(m.rollupCfgFor(M).exitMultiple)},
            upside {m.fmtX(m.rollupCfgFor(m.scenarioModel(M, "upside")).exitMultiple)},
            downside {m.fmtX(m.rollupCfgFor(m.scenarioModel(M, "downside")).exitMultiple)}.
          </div>
        )}
        <Computed label="Base case" value={`${M.sdeGrowth}% growth · ${m.fmtX(M.exitMult)} exit`} />
        <Computed label="Upside case" value={`${M.upGrowth}% growth · ${m.fmtX(M.exitMult + M.upExitDelta)} exit`} tone="go" />
        <Computed label="Downside case" value={`${M.downGrowth}% growth · ${m.fmtX(Math.max(1, M.exitMult - M.downExitDelta))} exit`} tone="pass" />
      </Card>
      )}

      {sel === "upload-parse" && <UploadParsePanel />}

      {sel === "revenue-mix" && (
      <Card title="Revenue Mix" testid="revenue-mix-card">
        <div className="bullet">Where revenue comes from, by customer or service type. Investors read this as concentration risk, so keep the shares adding to 100%.</div>
        <RevenueMixEditor mix={M.revenueMix} revenue={M.ttmRevenue} onChange={(mix) => patch({ revenueMix: mix })} />
      </Card>
      )}
      {sel === "leverage" && <LeveragePanel M={M} setField={setField} />}
      {sel === "working-capital" && <WorkingCapitalPanel M={M} setField={setField} />}

      {sel === "rollup-terms" && (
        <RollUpPanel M={M} V={V} setField={setField} patch={patch} consolidated={consolidated}
          onRollUp={() => navigate("rollup")} onViewConsolidated={() => { setScope(m.SCOPE_ROLLUP); navigate("returns"); }} />
      )}

      {sel === "distribution" && (
        <DistributionPolicyCard M={V} patch={patch} scenario="base" onRollUp={() => navigate("rollup")}
          onBackLever={() => setSel("back-lever")} />
      )}

      {sel === "back-lever" && <BackLeverCard M={M} V={m.scopedModel(M, M.rollup && m.scopeOptions(M) ? m.SCOPE_ROLLUP : scope)} patch={patch} />}

      <Card title={`Live Return Preview${viewTag ? ` — ${viewTag}` : ""}`} style={{ marginTop: 12 }} testid="live-preview">
        <div className="grid g3">
          <Tile label={consolidated ? "Total capital deployed" : "Net Acq. Cost"} value={m.fmt$(consolidated ? m.sourcesUses(V).totalUses : m.nac(V))} />
          <Tile label={consolidated ? "Consolidated entry multiple" : "Entry multiple"} value={m.fmtX(m.entryMultiple(V))} sub={consolidated ? "total price ÷ EBITDA" : "NAC / SDE"} testid="lp-entry" />
          <Tile label="DSCR Year 1" value={m.fmtX(dscr)} tone={m.dscrTone(dscr)} />
          <Tile label="Free Cash Flow Yr 1" value={m.fmt$(m.fcfYr1(V))} tone={m.fcfTone(m.fcfYr1(V))} />
          <Tile label="Exit EV" value={V.holdIndefinite ? "—" : m.fmt$(ex.exitEV)} sub={consolidated ? `${m.fmtX(V.exitMult)} consolidated exit` : undefined} />
          <Tile label="Equity MOIC" value={m.fmtX(ex.moic)} tone={m.moicTone(ex.moic)}
            sub={asmBump ? `QSBS +${m.fmtX(asmBump.x)} after tax` : undefined} testid="asm-eqmoic" />
          <Tile label="Deal IRR" value={isNaN(ex.irr) ? "n/a" : m.fmtPct(ex.irr)} tone={ex.irr >= 20 ? "go" : ex.irr >= 12 ? "amber" : "pass"}
            sub={asmBump && asmBump.bps > 0 ? `QSBS +${asmBump.bps} bps after tax` : undefined} testid="asm-dealirr" />
        </div>
      </Card>
      </div>
      </div>
    </div>
  );
}

// Add-on rows of the roll-up (year > 0) for this deal.
const rollupAddonRows = (M) => m.addonTableRows(M);

// Seller notes issued to each add-on seller, driven by the roll-up seller-note %, with optional
// rate/term overrides that are separate from the platform note.
function AddOnSellerNotes({ M, setField }) {
  const rows = rollupAddonRows(M);
  const R = m.rollupCfgFor(M);
  const rate = m.addonSnRate(M), yrs = m.addonSnYrs(M);
  const setR = (k, v) => setField("rollup", { ...R, [k]: v === "" ? null : Number(v) });
  const ds = (amt) => m.annuity(amt, rate, yrs);
  const tot = rows.reduce((a, r) => a + r.sellerNote, 0);
  return (
    <div data-testid="addon-seller-notes">
      <div className="section-label" style={{ marginTop: 14 }}>Add-on seller notes — {R.sellerNotePct}% of each add-on price</div>
      <div className="grid g2 ops-grid" style={{ marginBottom: 8 }}>
        <div className="field"><label>Add-on note rate % (blank = platform {M.snRate}%)</label>
          <input className="inp mono" inputMode="decimal" value={R.snRate == null ? "" : R.snRate} placeholder={String(M.snRate)}
            onChange={(e) => setR("snRate", e.target.value.replace(/[^0-9.]/g, ""))} data-testid="addon-sn-rate" /></div>
        <div className="field"><label>Add-on note term (yrs · blank = platform {M.snYrs})</label>
          <input className="inp mono" inputMode="numeric" value={R.snYrs == null ? "" : R.snYrs} placeholder={String(M.snYrs)}
            onChange={(e) => setR("snYrs", e.target.value.replace(/[^0-9]/g, ""))} data-testid="addon-sn-yrs" /></div>
      </div>
      {rows.length === 0
        ? <div className="ir-fine">No add-ons yet — add them under Assumptions → Roll-Up and their seller notes appear here.</div>
        : <table className="tbl tbl-tight" data-testid="addon-sn-table">
            <thead><tr><th>Add-on</th><th className="num">Closes</th><th className="num">Price</th><th className="num">Seller note</th><th className="num">Rate</th><th className="num">Term</th><th className="num">Annual payment</th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} data-testid={`addon-sn-row-${i}`}>
                  <td>{r.name}</td><td className="num mono">{r.yearLabel || `Yr ${r.year}`}</td><td className="num">{m.fmt$(r.price)}</td>
                  <td className="num">{m.fmt$(r.sellerNote)}</td><td className="num">{rate}%</td><td className="num">{yrs} yr</td><td className="num">{m.fmt$(ds(r.sellerNote))}</td>
                </tr>
              ))}
              <tr className="total"><td>All add-on notes</td><td className="num">—</td><td className="num">{m.fmt$(rows.reduce((a, r) => a + r.price, 0))}</td>
                <td className="num">{m.fmt$(tot)}</td><td className="num">{rate}%</td><td className="num">{yrs} yr</td><td className="num">{m.fmt$(ds(tot))}</td></tr>
            </tbody>
          </table>}
    </div>
  );
}

// The platform fee schedule charged on every add-on, funded inside each add-on equity check.
function AddOnCostsTable({ M }) {
  const rows = rollupAddonRows(M);
  if (!rows.length) return <div className="ir-fine" style={{ marginTop: 10 }} data-testid="addon-costs-empty">Add-ons inherit this same fee schedule — add them under Assumptions → Roll-Up to see the add-on fees.</div>;
  const fee = (r, k) => (k === "acq" ? ((M.acqFeePct || 0) / 100) * r.price : k === "close" ? ((M.closingCostPct || 0) / 100) * r.price
    : k === "eq" ? ((M.equityFeePct || 0) / 100) * r.equityBase : ((M.debtFeePct || 0) / 100) * (r.debt + r.sellerNote));
  const tot = (k) => rows.reduce((a, r) => a + fee(r, k), 0);
  return (
    <div data-testid="addon-costs">
      <div className="section-label" style={{ marginTop: 14 }}>Add-on transaction costs — same % charged on every add-on</div>
      <table className="tbl tbl-tight" data-testid="addon-costs-table">
        <thead><tr><th>Add-on</th><th className="num">Price</th><th className="num">Acq. fee {M.acqFeePct}%</th><th className="num">Closing {M.closingCostPct}%</th><th className="num">Equity fee {M.equityFeePct}%</th><th className="num">Debt fee {M.debtFeePct}%</th><th className="num">Total</th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} data-testid={`addon-cost-row-${i}`}>
              <td>{r.name} <span className="mono" style={{ color: "var(--text3)" }}>{r.yearLabel || `Yr ${r.year}`}</span></td>
              <td className="num">{m.fmt$(r.price)}</td><td className="num">{m.fmt$(fee(r, "acq"))}</td><td className="num">{m.fmt$(fee(r, "close"))}</td>
              <td className="num">{m.fmt$(fee(r, "eq"))}</td><td className="num">{m.fmt$(fee(r, "debt"))}</td><td className="num" style={{ fontWeight: 600 }}>{m.fmt$(r.fees)}</td>
            </tr>
          ))}
          <tr className="total"><td>All add-ons</td><td className="num">{m.fmt$(rows.reduce((a, r) => a + r.price, 0))}</td>
            <td className="num">{m.fmt$(tot("acq"))}</td><td className="num">{m.fmt$(tot("close"))}</td><td className="num">{m.fmt$(tot("eq"))}</td>
            <td className="num">{m.fmt$(tot("debt"))}</td><td className="num">{m.fmt$(rows.reduce((a, r) => a + r.fees, 0))}</td></tr>
        </tbody>
      </table>
      <div className="ir-fine" style={{ marginTop: 6 }}>These fees are funded inside each add-on's equity check (war chest → retained cash flow → capital call), so they hit the consolidated Sources &amp; Uses and IRR without being charged twice.</div>
    </div>
  );
}
